import { FC } from 'react'
import * as React from 'react'
import { AddIcon } from '@chakra-ui/icons'
import { CalendarEvent } from '../../services/calendar'
import CalendarEventRow from './CalendarEventRow'
import { buildMonthDays, eventChipClass, shortDateLabel } from './calendar-layout'

const WEEKDAYS = ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB']

/** Quantos eventos cabem numa célula antes do "+N mais". */
const EVENTS_PER_CELL = 3

interface CalendarMonthViewProps {
  year: number
  month: number
  events: CalendarEvent[]
  selectedDate: string | null
  onSelectDate: (date: string) => void
  onCreate: (date: string) => void
  onOpenEvent: (event: CalendarEvent) => void
}

const CalendarMonthView: FC<CalendarMonthViewProps> = ({
  year,
  month,
  events,
  selectedDate,
  onSelectDate,
  onCreate,
  onOpenEvent,
}) => {
  const days = buildMonthDays(year, month, events)
  const selectedEvents = selectedDate ? events.filter(event => event.date === selectedDate) : []

  return (
    <div className="mt-4 flex flex-col gap-4 lg:flex-row">
      <div className="flex-1 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
          {WEEKDAYS.map(weekday => (
            <div
              key={weekday}
              className="border-r border-slate-200 py-2 text-center text-[11px] font-semibold tracking-wide text-slate-500 last:border-r-0"
            >
              {weekday}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7">
          {days.map(day => (
            <div
              key={day.date}
              onClick={() => onSelectDate(day.date)}
              className={`group relative min-h-[5.5rem] cursor-pointer border-b border-r border-slate-200 p-1.5 transition-colors sm:min-h-[7.5rem] sm:p-2 ${
                day.isCurrentMonth ? 'bg-white' : 'bg-slate-50/60'
              } ${selectedDate === day.date ? 'z-10 ring-2 ring-inset ring-secondary-pure' : ''} hover:bg-slate-50`}
            >
              <div className="mb-1 flex items-center justify-between">
                <button
                  type="button"
                  title="Criar bloqueio neste dia"
                  onClick={quietly(() => onCreate(day.date))}
                  className="hidden h-5 w-5 items-center justify-center rounded text-slate-300 opacity-0 transition hover:bg-slate-100 hover:text-secondary-pure group-hover:opacity-100 sm:flex"
                >
                  <AddIcon boxSize={2} />
                </button>
                {day.isToday ? (
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-secondary-pure text-xs font-semibold text-white">
                    {day.day}
                  </span>
                ) : (
                  <span
                    className={`text-xs font-medium ${day.isCurrentMonth ? 'text-slate-600' : 'text-slate-300'}`}
                  >
                    {day.day}
                  </span>
                )}
              </div>

              <div className="space-y-0.5">
                {day.events.slice(0, EVENTS_PER_CELL).map(event => (
                  <button
                    key={`${event.id}-${event.date}`}
                    type="button"
                    onClick={quietly(() => onOpenEvent(event))}
                    className={`w-full truncate rounded px-1 py-0.5 text-left text-[10px] font-medium leading-tight transition hover:scale-[1.02] sm:px-1.5 sm:text-[11px] ${eventChipClass(
                      event.color,
                    )}`}
                  >
                    {event.starts_at && !event.is_flexible && (
                      <span className="opacity-80">{event.starts_at} </span>
                    )}
                    {event.display_title}
                  </button>
                ))}
                {day.events.length > EVENTS_PER_CELL && (
                  <button
                    type="button"
                    onClick={quietly(() => onSelectDate(day.date))}
                    className="px-1 text-[10px] text-slate-500 hover:underline"
                  >
                    +{day.events.length - EVENTS_PER_CELL} mais
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="w-full shrink-0 lg:w-80">
        {selectedDate ? (
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm lg:sticky lg:top-4">
            <div className="mb-3 flex items-center justify-between">
              <h4 className="text-sm font-semibold capitalize text-slate-700">
                {shortDateLabel(selectedDate)}
              </h4>
              <button
                type="button"
                onClick={() => onCreate(selectedDate)}
                className="rounded-full bg-secondary-pure px-3 py-1.5 text-xs font-medium text-white"
              >
                + Criar
              </button>
            </div>
            {selectedEvents.length === 0 ? (
              <p className="py-6 text-center text-xs text-slate-400">Nenhum evento neste dia.</p>
            ) : (
              <div className="max-h-[60vh] space-y-2 overflow-y-auto">
                {selectedEvents.map(event => (
                  <CalendarEventRow key={`${event.id}-${event.date}`} event={event} onClick={onOpenEvent} />
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-400">
            Toque em um dia para ver e criar eventos
          </div>
        )}
      </div>
    </div>
  )
}

/** Clique no filho não deve disparar o clique de seleção do dia. */
function quietly(action: () => void) {
  return (event: React.MouseEvent) => {
    event.stopPropagation()
    action()
  }
}

export default CalendarMonthView
